// «Ημερολόγιο κινητού» (owner 22 Oct 2026): the advisor's appointments and day plan as private iCalendar subscription
// feeds (RFC 5545 + RFC 7986), served by the platform from the rows it already holds through sync
// (packages/platform/src/calendar-feeds.ts). One feed PER CATEGORY, so every category is its own subscribed calendar
// on the phone — iOS and Google Calendar give a colour per calendar, not per event — plus one combined «Όλα» feed.
//
//   appointments  CRM appointments (tasks.kind «appointment» with a time) and planned meetings
//   plan          the advisor's own blocks: relationship / referral meetings, preparation, learning, team, admin, travel …
//   calls         calls and follow-ups: call blocks, accepted call / follow-up goals, timed call and follow-up tasks
//   personal      breaks and personal time
//
// Privacy by design: the feed URL is a bearer secret (anyone with it can read the feed), so the text is minimal. Never
// notes, health, policy or financial details. By default (`detail: 'initials'`) an event says only its type and the
// client's initials («Ραντεβού · Μ.Γ.»); `detail: 'full'` adds the advisor's own title, the client's name and the
// location. Times are Europe/Nicosia wall-clock times with a VTIMEZONE; UIDs are stable per row and feed; SEQUENCE is
// the row's version and LAST-MODIFIED its updatedAt, so a phone updates an event in place.
import type { WorkspaceView } from './changeset.ts';
import { addDays, isDay, type Day } from './dates.ts';
import { feedCategoryOf, fromMin, localDayOf, localTimeOf, planTypeOf, taskEndTime, toMin, type FeedCategory, type PlanType } from './planner.ts';
import { getOffice } from './setup.ts';

export const FEED_KEYS = ['all', 'appointments', 'plan', 'calls', 'personal'] as const;
export type FeedKey = typeof FEED_KEYS[number];
export type FeedLang = 'el' | 'en' | 'ru';
export type FeedDetail = 'initials' | 'full';
export const FEED_TZ = 'Europe/Nicosia';
/** colour hints: RFC 7986 COLOR takes a CSS3 colour NAME; Apple reads X-APPLE-CALENDAR-COLOR (hex of the same colour) */
export const FEED_META: Record<FeedKey, { css: string; hex: string; name: Record<FeedLang, string> }> = {
  all: { css: 'midnightblue', hex: '#191970', name: { el: 'Όλα', en: 'All', ru: 'Всё' } },
  appointments: { css: 'teal', hex: '#008080', name: { el: 'Ραντεβού', en: 'Appointments', ru: 'Встречи' } },
  plan: { css: 'goldenrod', hex: '#DAA520', name: { el: 'Πρόγραμμα', en: 'Day plan', ru: 'План дня' } },
  calls: { css: 'royalblue', hex: '#4169E1', name: { el: 'Τηλεφωνήματα & follow-ups', en: 'Calls & follow-ups', ru: 'Звонки и follow-up' } },
  personal: { css: 'slategray', hex: '#708090', name: { el: 'Διαλείμματα & προσωπικά', en: 'Breaks & personal', ru: 'Перерывы и личное' } },
};
export const feedName = (key: FeedKey, lang: FeedLang = 'el') => `AVRASTRO · ${FEED_META[key].name[lang] ?? FEED_META[key].name.el}`;
/** the type labels of the events (the platform has no i18n bundle; the app's labels are planner.type.* in i18n) */
const TYPE_LABEL: Record<PlanType, Record<FeedLang, string>> = {
  client_appointment: { el: 'Ραντεβού', en: 'Appointment', ru: 'Встреча' },
  coffee: { el: 'Καφές / γνωριμία', en: 'Coffee / getting to know', ru: 'Кофе / знакомство' },
  referral_meeting: { el: 'Συνάντηση για σύσταση', en: 'Referral meeting', ru: 'Встреча по рекомендации' },
  calls: { el: 'Τηλεφωνήματα', en: 'Calls', ru: 'Звонки' },
  followup: { el: 'Follow-up', en: 'Follow-up', ru: 'Follow-up' },
  admin: { el: 'Email & γραφειακά', en: 'Email & admin', ru: 'Почта и офис' },
  prep: { el: 'Προετοιμασία', en: 'Preparation', ru: 'Подготовка' },
  learning: { el: 'Εκπαίδευση / μελέτη', en: 'Learning', ru: 'Обучение' },
  team: { el: 'Ομάδα / σύσκεψη', en: 'Team meeting', ru: 'Команда / совещание' },
  travel: { el: 'Μετακίνηση', en: 'Travel', ru: 'Дорога' },
  break: { el: 'Διάλειμμα', en: 'Break', ru: 'Перерыв' },
  personal: { el: 'Προσωπικό', en: 'Personal', ru: 'Личное' },
  other: { el: 'Άλλο', en: 'Other', ru: 'Другое' },
  focus: { el: 'Συγκέντρωση', en: 'Focus time', ru: 'Сосредоточенная работа' },
  task: { el: 'Εργασία', en: 'Task', ru: 'Задача' },
  goal: { el: 'Στόχος', en: 'Goal', ru: 'Цель' },
};
export const feedTypeLabel = (t: PlanType, lang: FeedLang = 'el') => TYPE_LABEL[t]?.[lang] ?? TYPE_LABEL[t]?.el ?? t;

export interface FeedEvent {
  uid: string; day: Day; start: string; end: string; summary: string; description: string; location: string | null;
  category: FeedCategory; type: PlanType; sequence: number; created: string; lastModified: string; done: boolean;
}
export interface FeedOptions {
  detail: FeedDetail; lang: FeedLang;
  /** the window (local days); default: 30 days back, 180 ahead of `today` */
  today: Day; from?: Day; to?: Day;
  /** rows this member may see in the feed (the platform's per-member scope); default all */
  mine?: (table: 'tasks' | 'meetings' | 'plan_blocks', id: string) => boolean;
  /** the app address (a link into the day in the description) */
  appUrl?: string | null;
}
/** «Maria Georgiou» → «M.G.» (first letters of the first two words) */
export function initialsOf(name: string | null | undefined): string {
  const w = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  return w.slice(0, 2).map((x) => `${[...x][0]!.toLocaleUpperCase()}.`).join('');
}

/** every event of the window, in all categories (filter by `category` for one feed) */
export function feedEvents(v: WorkspaceView, o: FeedOptions): FeedEvent[] {
  const from = o.from ?? addDays(o.today, -30), to = o.to ?? addDays(o.today, 180);
  const mine = o.mine ?? (() => true);
  const lang = o.lang;
  const out: FeedEvent[] = [];
  const who = (personId?: string | null, prospectId?: string | null) => {
    const p = personId ? v.get('persons', personId) : undefined;
    const q = !p && prospectId ? v.get('prospects', prospectId) : undefined;
    return p?.displayName ?? q?.name ?? '';
  };
  const text = (type: PlanType, title: string, name: string, done: boolean) => {
    const label = feedTypeLabel(type, lang);
    let s: string;
    if (o.detail === 'full') { const t = title.trim() || label; s = name && !t.includes(name) ? `${t} · ${name}` : t; }
    else { const ini = initialsOf(name); s = ini ? `${label} · ${ini}` : label; }
    return (done ? '✓ ' : '') + s.slice(0, 200);
  };
  const link = (day: Day) => (o.appUrl ? `\n${o.appUrl.replace(/\/+$/, '')}/#/agenda?day=${day}` : '');
  const desc = (type: PlanType, day: Day) => `AVRASTRO CRM · ${feedTypeLabel(type, lang)}. ${lang === 'en' ? 'Details in the CRM.' : lang === 'ru' ? 'Подробности в CRM.' : 'Λεπτομέρειες στο CRM.'}${link(day)}`;
  const inWindow = (d: string | null | undefined): d is Day => !!d && isDay(d) && d >= from && d <= to;
  const apptTasks = new Set<string>();
  for (const t of v.rows('tasks')) {
    if (!inWindow(t.dueDate) || !t.dueTime || !Number.isFinite(toMin(t.dueTime.slice(0, 5))) || t.status === 'cancelled') continue;
    if (t.kind === 'appointment') apptTasks.add(t.id);
    if (!mine('tasks', t.id)) continue;
    const type = planTypeOf({ source: t.kind === 'appointment' ? 'appointment' : 'task', kind: t.kind });
    const name = who(t.personId, t.prospectId), done = t.status === 'done';
    out.push({
      uid: `tasks.${t.id}`, day: t.dueDate, start: t.dueTime.slice(0, 5), end: taskEndTime(v, t), summary: text(type, t.title, name, done), description: desc(type, t.dueDate),
      location: o.detail === 'full' && t.location ? t.location.slice(0, 200) : null, category: feedCategoryOf(type), type, sequence: t.version, created: t.createdAt, lastModified: t.updatedAt, done,
    });
  }
  const meetingMin = getOffice(v).meetingMinutes;
  for (const m of v.rows('meetings')) {
    if (m.status !== 'planned' || (m.taskId && apptTasks.has(m.taskId)) || !mine('meetings', m.id)) continue;
    const day = localDayOf(m.at), start = localTimeOf(m.at);
    if (!inWindow(day) || !start) continue;
    out.push({
      uid: `meetings.${m.id}`, day, start, end: fromMin(toMin(start) + meetingMin), summary: text('client_appointment', m.purpose ?? '', who(m.personId), false), description: desc('client_appointment', day),
      location: null, category: 'appointments', type: 'client_appointment', sequence: m.version, created: m.createdAt, lastModified: m.updatedAt, done: false,
    });
  }
  for (const b of v.rows('plan_blocks')) {
    if (b.status === 'cancelled' || !inWindow(b.day) || !mine('plan_blocks', b.id)) continue;
    const type = planTypeOf({ source: 'block', kind: b.kind, activity: b.activity, goalType: b.goalType });
    const name = b.personId ? who(b.personId) : b.taskId ? (() => { const t = v.get('tasks', b.taskId!); return t ? who(t.personId, t.prospectId) : ''; })() : '';
    out.push({
      uid: `plan_blocks.${b.id}`, day: b.day, start: b.start, end: b.end, summary: text(type, b.title, name, b.status === 'done'), description: desc(type, b.day),
      location: null, category: feedCategoryOf(type), type, sequence: b.version, created: b.createdAt, lastModified: b.updatedAt, done: b.status === 'done',
    });
  }
  return out.sort((a, b) => a.day.localeCompare(b.day) || a.start.localeCompare(b.start) || a.uid.localeCompare(b.uid));
}

// --- iCalendar text ----------------------------------------------------------------------------------------------
/** TEXT value escaping (RFC 5545 §3.3.11) */
export const icalEscape = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');
/** content lines are folded at 75 octets (UTF-8), never inside a character (RFC 5545 §3.1) */
export function icalFold(line: string): string {
  const enc = new TextEncoder();
  if (enc.encode(line).length <= 75) return line;
  const parts: string[] = [];
  let cur = '', n = 0, limit = 75;
  for (const ch of line) {
    const b = enc.encode(ch).length;
    if (n + b > limit) { parts.push(cur); cur = ''; n = 0; limit = 74; /* the leading space counts */ }
    cur += ch; n += b;
  }
  if (cur) parts.push(cur);
  return parts.join('\r\n ');
}
const utcStamp = (iso: string) => { const d = new Date(iso); return isNaN(d.getTime()) ? '19700101T000000Z' : d.toISOString().replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z'); };
const local = (day: Day, hhmm: string) => `${day.replace(/-/g, '')}T${hhmm.replace(':', '')}00`;
/** Europe/Nicosia (EU rules: last Sunday of March 03:00 → EEST, last Sunday of October 04:00 → EET) */
export const VTIMEZONE_NICOSIA = [
  'BEGIN:VTIMEZONE', `TZID:${FEED_TZ}`, `X-LIC-LOCATION:${FEED_TZ}`,
  'BEGIN:DAYLIGHT', 'TZOFFSETFROM:+0200', 'TZOFFSETTO:+0300', 'TZNAME:EEST', 'DTSTART:19700329T030000', 'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU', 'END:DAYLIGHT',
  'BEGIN:STANDARD', 'TZOFFSETFROM:+0300', 'TZOFFSETTO:+0200', 'TZNAME:EET', 'DTSTART:19701025T040000', 'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU', 'END:STANDARD',
  'END:VTIMEZONE',
];
/** one feed as text/calendar (CRLF lines, folded) — `key` 'all' carries every category */
export function feedIcs(events: readonly FeedEvent[], key: FeedKey, o: { lang: FeedLang; now: string; refreshMinutes?: number }): string {
  const meta = FEED_META[key];
  const ttl = `PT${Math.max(15, Math.round(o.refreshMinutes ?? 60))}M`;
  const name = feedName(key, o.lang);
  const lines = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//AVRASTRO//CRM calendar feed//EL', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    `NAME:${icalEscape(name)}`, `X-WR-CALNAME:${icalEscape(name)}`,
    `X-WR-CALDESC:${icalEscape(o.lang === 'en' ? 'Private AVRASTRO CRM calendar (read only). Changes are made in the CRM.' : o.lang === 'ru' ? 'Личный календарь AVRASTRO CRM (только чтение). Изменения вносятся в CRM.' : 'Ιδιωτικό ημερολόγιο του AVRASTRO CRM (μόνο ανάγνωση). Οι αλλαγές γίνονται στο CRM.')}`,
    `X-WR-TIMEZONE:${FEED_TZ}`, `REFRESH-INTERVAL;VALUE=DURATION:${ttl}`, `X-PUBLISHED-TTL:${ttl}`,
    `COLOR:${meta.css}`, `X-APPLE-CALENDAR-COLOR:${meta.hex}`,
    ...VTIMEZONE_NICOSIA,
  ];
  const stamp = utcStamp(o.now);
  for (const e of events) {
    if (key !== 'all' && e.category !== key) continue;
    // an end at or before the start (a block ending at 23:59) is kept one minute long
    const end = toMin(e.end) > toMin(e.start) ? e.end : fromMin(toMin(e.start) + 1);
    lines.push('BEGIN:VEVENT', `UID:${e.uid}.${key}@avrastro.com`, `DTSTAMP:${stamp}`, `CREATED:${utcStamp(e.created)}`, `LAST-MODIFIED:${utcStamp(e.lastModified)}`, `SEQUENCE:${Math.max(0, Math.round(e.sequence) || 0)}`,
      `DTSTART;TZID=${FEED_TZ}:${local(e.day, e.start)}`, `DTEND;TZID=${FEED_TZ}:${local(e.day, end)}`,
      `SUMMARY:${icalEscape(e.summary)}`, `DESCRIPTION:${icalEscape(e.description)}`, ...(e.location ? [`LOCATION:${icalEscape(e.location)}`] : []),
      `CATEGORIES:${icalEscape(feedTypeLabel(e.type, o.lang))}`, 'STATUS:CONFIRMED', `TRANSP:${e.category === 'personal' && e.type === 'break' ? 'TRANSPARENT' : 'OPAQUE'}`, 'CLASS:PRIVATE', 'END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return lines.map(icalFold).join('\r\n') + '\r\n';
}
